import { common } from '../gads'
import {
    Account,
    AddCallExtension,
    AddNegativeGeoKeyword,
    AddRobotNegative,
    AddSiteExclusions,
    AddSitelinkExtension,
    AddSkagV2,
    AddStructuredSnippetsExtension,
    AdjustAdGroupMultiTargets,
    AdjustAgeBid,
    AdjustAttributionModel,
    AdjustDeviceBid,
    AdjustDeviceBidsV2,
    AdjustGenderBid,
    AdjustGeoBids,
    AdjustKeywordBidV2,
    AdjustParentalBid,
    AdjustTarget,
    CampaignLevelExtensions,
    CheckAdSpelling,
    CheckProductGroups,
    CheckQueryRelevance,
    CheckQueryRelevanceMS,
    ContentExclusion,
    DemoBids,
    DisableInterestLocations,
    DisableInterestLocationsV2,
    DisableSearchPartners,
    DuplicateKeywords,
    Entity,
    ExcludeMobileApps,
    ExcludePlacement,
    FixBelowFirstPageBid,
    FixBrokenLink,
    GoogleAdsSync,
    KeywordBidsSyncImprovement,
    LiftCampaignBudgetCap,
    LocationBids,
    Ngram,
    PauseCampaignSyncImprovement,
    PauseKeyword,
    Platform,
    ReduceCampaignBids,
    RemoveBroadKeywords,
    RemovePlussedNegatives,
    SearchPartnerSettings,
    SettingFlippers,
    UUID,
} from '..'
import { Billing } from '../billing/billing'
import { User } from '../user/user'

import {
    ImprovementLocations,
    type ImprovementLocationTuple,
    type ImprovementMultiLocationTuple,
} from './locations'
import { PauseAd } from './types/ad-comparison-v2'
import { ReplaceBroadKeywordV2 } from './types/broad-keywords-v2/remove_broad_keywords_v2'

// Types used in improvement.ts and across improvement implementation files
export namespace Improvement {
    /**
     * @name ID
     * @description ID for an improvement
     */
    export type ID = number

    /**
     * @name SyncType
     * @description Sync only improvement types (filename)
     */
    export const SyncType = {
        GoogleAdsSync: 'google_ads_sync',
        SyncPauseCampaign: 'sync_pause_campaign',
        SyncKeywordBids: 'sync_keyword_bids',
    } as const

    /**
     * @name Type
     * @description All improvement types (filename)
     */
    export const Type = {
        // Special non-existent type
        NotExists: 'not_exists',
        // Special meta improvement (generates all other imps)
        Meta: 'refresh_cycling_improvements',

        // -- Improvements
        // Shared types
        AdComparison: 'ad_comparison',
        AdComparisonV2: 'ad_comparison_v2',
        Branding: 'branding',
        Remarketing: 'remarketing',
        BroadKeywords: 'broad_keywords',
        BroadKeywordsV2: 'broad_keywords_v2',
        BudgetCap: 'budget_cap',
        ContentExclusions: 'content_exclusions',
        LocationInterestV2: 'location_interest_v2',
        SQR: 'sqr',
        SqrShopping: 'sqr_shopping',
        SKAGV1: 'skag_v1',
        SKAG: 'skag',
        KeywordCutoff: 'keyword_cutoff',
        KeywordBids: 'keyword_bids',
        KeywordBidsV2: 'keyword_bids_v2',
        BrokenLink: 'broken_link',
        DemographicBids: 'demo_bids',
        DemographicGenderMod: 'demographic_gender_mod',
        DemographicParentalMod: 'demographic_parental_mod',
        DemographicAgeMod: 'demographic_age_mod',
        PlacementExclusion: 'placement_exclusion',
        MobileAppsExclusion: 'mobile_apps_exclusion',
        PauseDuplicateKeywords: 'pause_duplicate_keywords',
        PauseDuplicateKeywordsV2: 'pause_duplicate_keywords_v2',
        SiteCategoryExclusions: 'site_category_exclusions',
        FrequencyCapping: 'frequency_capping',
        SearchPartnersSettings: 'search_partners_settings',
        SearchPartners: 'search_partners', // v2
        SpellChecker: 'spell_checker',
        AdSchedule: 'ad_schedule',
        AdScheduleV2: 'ad_schedule_v2',
        RemovePlussedNegatives: 'remove_plussed_negatives',
        CheckNegativesBlockingKeywords: 'check_negatives_blocking_keywords',
        GeoBids: 'geo_bids',
        LocationBids: 'location_bids',
        ResyncSharedSetNegativeLists: 'resync_shared_set_negative_lists',
        LocationInterests: 'location_interests',
        NegativeGeoKeywords: 'negative_geo_keywords',
        BidsBelowFirstPage: 'bids_below_first_page',
        CampaignLevelExtensions: 'campaign_level_extensions',
        AccountLevelExtensions: 'account_level_extensions',
        ResyncExclusionLists: 'resync_exclusion_lists',
        AttributionModel: 'attribution_model',
        DeviceBids: 'device_bids',
        RobotNegatives: 'robot_negatives',
        CheckProductGroups: 'check_product_groups',
        Ngram: 'ngram',
        DeviceBidsV2: 'device_bids_v2',
        TargetAdjustments: 'target_adjustments',
        // Microsoft Ads types only
        KeywordBidsMs: 'keyword_bids_ms',
        SQRMs: 'sqr_ms',
        // --- Issues ---
        // Critical Issues
        SpendFlatlineIssue: 'spend_flatline_issue',
        ConversionFlatlineIssue: 'conversion_flatline_issue',
        PaymentCardExpiringIssue: 'payment_card_expiring_issue',
        PaymentCardExpiredIssue: 'payment_card_expired_issue',
        // Broken Link Issues
        BrokenLinkIssue: 'broken_link_issue', // For general broken links that are issues
        BrokenSitelinkIssue: 'broken_sitelink_issue',
        // Disapproval Issues
        DisapprovedEntityIssue: 'disapproved_entity_issue',
        // Account Management Issues
        EmptyEntityIssue: 'empty_entity_issue',
        LowActivityReactivationIssue: 'low_activity_reactivation_issue',
        FailedLearningIssue: 'failed_learning_issue',
        // Spelling Error Issues
        SpellingErrorIssue: 'spelling_error_issue',
        ...SyncType,
        // Multi-platform types
        SegmentExclusions: 'segment_exclusions',
        SettingFlippers: 'setting_flippers',
    } as const

    /**
     * @name RecAction
     * @description All improvement rec actions (push action, can be >1 per type)
     */
    export const RecAction = {
        // Google
        AddCallExtension: 'add_call_extension',
        AddCalloutExtension: 'add_callout_extension',
        AddCampaignLevelCalloutExtension: 'add_campaign_level_callout_extension',
        AddCampaignLevelSitelinkExtension: 'add_campaign_level_sitelink_extension',
        AddCampaignLevelSnippetExtension: 'add_campaign_level_snippet_extension',
        AddFrequencyCapping: 'add_frequency_capping',
        AddMissingCampaignsToSharedSet: 'add_missing_campaigns_to_shared_set',
        AddLocationNameAsNegative: 'add_location_name_as_negative',
        AddNegativeListShopping: 'add_negative_list_shopping',
        AddNegativeWord: 'add_negative_word',
        AddRobotNegative: 'add_robot_negative',
        AddSiteExclusions: 'add_site_exclusions',
        AddSitelinkExtension: 'add_sitelink_extension',
        AddSkagV1: 'add_skag',
        AddSkagV2: 'add_skag_v2',
        AddStructuredSnippetsExtension: 'add_structured_snippets_extension',
        AdjustAdGroupMultiTargets: 'adjust_ad_group_multi_targets',
        AdjustAdScheduleBids: 'adjust_ad_schedule_bids',
        AdjustAdScheduleBidsV2: 'adjust_ad_schedule_bids_v2',
        AdjustSingleAgeBid: 'adjust_single_age_bid',
        AdjustSingleGenderBid: 'adjust_single_gender_bid',
        AdjustSingleParentalStatusBid: 'adjust_single_parental_status_bid',
        AdjustSingleIncomeBid: 'adjust_single_income_bid',
        ExcludeAgeDemographic: 'exclude_age_demographic',
        ExcludeGenderDemographic: 'exclude_gender_demographic',
        ExcludeParentalStatusDemographic: 'exclude_parental_status_demographic',
        ExcludeIncomeDemographic: 'exclude_income_demographic',
        AdjustMultipleAgeBids: 'adjust_multiple_age_bids',
        AdjustMultipleGenderBids: 'adjust_multiple_gender_bids',
        AdjustMultipleParentalStatusBids: 'adjust_multiple_parental_status_bids',
        AdjustMultipleIncomeBids: 'adjust_multiple_income_bids',
        AdjustAgeBid: 'adjust_age_bid',
        AdjustAttributionModel: 'adjust_attribution_model',
        AdjustDeviceBid: 'adjust_device_bid',
        AdjustGenderBid: 'adjust_gender_bid',
        AdjustGeoBids: 'adjust_geo_bids',
        AdjustLocationBids: 'adjust_location_bids',
        ExcludeLocation: 'exclude_location',
        AdjustKeywordBid: 'adjust_keyword_bid',
        AdjustKeywordBidV2: 'adjust_keyword_bid_v2',
        AdjustParentalBid: 'adjust_parental_bid',
        AdjustProductGroupBid: 'adjust_product_group_bid',
        CheckAdSpelling: 'check_ad_spelling',
        CheckQueryRelevance: 'check_query_relevance',
        CreateBrandCampaign: 'create_brand_campaign',
        CreateRemarketingCampaign: 'create_remarketing_campaign',
        DisableInterestLocations: 'disable_interest_locations',
        DisableInterestLocationsV2: 'disable_interest_locations_v2',
        DisableSearchPartners: 'disable_search_partners',
        DuplicateKeyword: 'duplicate_keyword',
        ExcludeWebsitePlacement: 'exclude_website_placement',
        ExcludeMobileAppPlacement: 'exclude_mobile_app_placement',
        ExcludeYoutubePlacement: 'exclude_youtube_placement',
        ExcludeGmailPlacement: 'exclude_gmail_placement',
        ExcludePlacement: 'exclude_placement',
        ExcludeMobileApps: 'exclude_mobile_apps',
        FixBelowFirstPageBid: 'fix_below_first_page_bid',
        FixBrokenLink: 'fix_broken_link',
        FixBrokenSitelink: 'fix_broken_sitelink',
        FixNegativesBlockingKeywords: 'fix_negatives_blocking_keywords',
        IncreaseKeywordBidV2: 'increase_keyword_bid_v2',
        LiftCampaignBudgetCap: 'lift_campaign_budget_cap',
        PauseAd: 'pause_ad',
        PauseAdV2: 'pause_ad_v2',
        PauseDuplicateKeywords: 'pause_duplicate_keywords',
        PauseDuplicateKeywordsV2: 'pause_duplicate_keywords_v2',
        PauseKeyword: 'pause_keyword',
        PauseKeywordV2: 'pause_keyword_v2',
        ReduceCampaignBids: 'reduce_campaign_bids',
        ReduceWordBids: 'reduce_word_bids',
        RemoveBroadKeywords: 'remove_broad_keywords',
        RemoveBroadKeywordsV2: 'remove_broad_keywords_v2',
        RemovePlussedNegatives: 'remove_plussed_negatives',
        ResyncExclusionLists: 'resync_exclusion_lists',
        ResyncProductGroups: 'resync_product_groups',
        SearchPartnersSettings: 'search_partners_settings',
        SubdivideProductGroup: 'subdivide_product_group',
        WriteAd: 'write_ad',
        WriteAdV2: 'write_ad_v2',
        AddNegativeNgram: 'add_negative_ngram',
        ReduceNgramBids: 'reduce_ngram_bids',
        AdjustSingleDeviceBid: 'adjust_single_device_bid',
        AdjustMultipleDeviceBids: 'adjust_multiple_device_bids',
        ExcludeSingleDevice: 'exclude_single_device',

        // Microsoft
        CheckQueryRelevanceMs: 'check_query_relevance_ms',
        PauseKeywordMs: 'pause_keyword_ms',
        AdjustKeywordBidMs: 'adjust_keyword_bid_ms',
        IncreaseKeywordBidMs: 'increase_keyword_bid_ms',

        // Multi-Platform

        // Exclude Segment (Google)
        ExcludeSegmentCountryGA: 'exclude_country_ga',
        ExcludeSegmentStateGA: 'exclude_us_state_ga',
        ExcludeSegmentDeviceGA: 'exclude_device_ga',
        ExcludeSegmentContentNetworkGA: 'disable_content_network_ga',
        ExcludeSegmentSearchPartnersGA: 'disable_search_partners_ga',
        ExcludeSegmentAgeDemoGA: 'exclude_age_demo_ga',
        ExcludeSegmentGenderGA: 'exclude_gender_ga',
        ExcludeSegmentIncomeGA: 'exclude_income_ga',
        ExcludeSegmentParentalStatusGA: 'exclude_parental_status_ga',
        ExcludeSegmentCityGA: 'exclude_city_ga',
        ExcludeSegmentLandingPageGA: 'exclude_landing_page_ga',
        ExcludeSegment: 'exclude_segment',

        // Exclude Segment (Microsoft)
        ExcludeSegmentCountryMS: 'exclude_country_ms',
        ExcludeSegmentStateMS: 'exclude_us_state_ms',
        ExcludeSegmentDeviceMS: 'exclude_device_ms',
        ExcludeSegmentSyndicatedSearchMS: 'disable_syndicated_search_ms',
        ExcludeSegmentAgeDemoMS: 'exclude_age_demo_ms',
        ExcludeSegmentGenderMS: 'exclude_gender_ms',
        ExcludeSegmentCityMS: 'exclude_city_ms',

        // Exclude Segment (Meta)
        ExcludeSegmentCountryME: 'exclude_country_me',
        ExcludeSegmentDeviceME: 'exclude_device_me',
        ExcludeSegmentPublisherPlatformME: 'exclude_publisher_platform_me',
        ExcludeSegmentAgeDemoME: 'exclude_age_demo_me',
        ExcludeSegmentGenderME: 'exclude_gender_me',

        // Exclude Segment (LinkedIn)
        ExcludeSegmentCountryLI: 'exclude_country_li',
        ExcludeSegmentStateLI: 'exclude_us_state_li',
        ExcludeSegmentOffsiteDeliveryLI: 'disable_offsite_delivery_li',
        ExcludeSegmentCompanyLI: 'exclude_company_li',
        ExcludeSegmentCompanySizeLI: 'exclude_company_size_li',
        ExcludeSegmentMemberIndustryLI: 'exclude_member_industry_li',
        ExcludeSegmentMemberJobFunctionLI: 'exclude_member_job_function_li',
        ExcludeSegmentMemberJobTitleLI: 'exclude_member_job_title_li',
        ExcludeSegmentMemberSeniorityLI: 'exclude_member_seniority_li',

        // Setting Flipper (Google)
        GADisableAdGroupOptimizedTargetingEnabled:
            'ga_disable_ad_group_optimized_targeting_enabled',
        GADisableBroadMatchAsDefault: 'ga_disable_broad_match_as_default',
        GAOptOutTextAssetAutomation: 'ga_opt_out_text_asset_automation',
        GAAccountRecommendLimitedBrandSafetySuitability:
            'ga_account_recommend_limited_brand_safety_suitability',
        GAAccountToggleRecommendationSubscriptions:
            'ga_account_toggle_recommendation_subscriptions',

        // Setting Flipper (Microsoft)
        MSAccountGetAutoApplyOptInStatus: 'ms_account_get_auto_apply_opt_in_status',
        MSCampaignPeopleInOrSearchingForOrViewingPages:
            'ms_campaign_people_in_or_searching_for_or_viewing_pages',
        MSCampaignAutoGeneratedTextOptOut: 'ms_campaign_auto_generated_text_opt_out',
        MSCampaignFinalUrlExpansionOptOut: 'ms_campaign_final_url_expansion_opt_out',
        MSCampaignAutoGeneratedImageOptOut: 'ms_campaign_auto_generated_image_opt_out',
        MSCampaignAutoGeneratedAssetsEnabled: 'ms_campaign_auto_generated_assets_enabled',

        // Setting Flipper (Meta)
        DisableImageTouchups: 'disable_image_touchups',
        DisableImageTemplates: 'disable_image_templates',
        DisableTextOptimizations: 'disable_text_optimizations',
        DisableVideoAutoCrop: 'disable_video_auto_crop',

        // Setting Flipper (Linkedin)
        DisableAudienceExpansionLi: 'disable_audience_expansion_li',
        AdjustCreativeSelectionLi: 'adjust_creative_selection_li',
        AdjustAdTargetingFacetLocationLi: 'adjust_ad_targeting_facet_location_li',

        // Issues
        RecheckDisapprovedAd: 'recheck_disapproved_ad',
        RecheckDisapprovedKeyword: 'recheck_disapproved_keyword',
        RecheckEmptyCampaign: 'recheck_empty_campaign',
        RecheckEmptyAdGroup: 'recheck_empty_ad_group',
        RecheckBrokenLink: 'recheck_broken_link',
        RecheckFailedLearning: 'recheck_failed_learning',
        RecheckSpellingError: 'recheck_spelling_error',
        RecheckReactivatedKeyword: 'recheck_reactivated_keyword',
        RecheckReactivatedAdGroup: 'recheck_reactivated_ad_group',
    } as const

    export type Category =
        | 'keywords'
        | 'negatives'
        | 'exclusions'
        | 'adjustments'
        | 'deprecated'
        | 'critical_issues'
        | 'broken_links'
        | 'disapprovals'
        | 'account_management'
        | 'spelling_errors'

    // Convert enum objects to literal types
    export type Type = (typeof Type)[keyof typeof Type]
    export type RecAction = (typeof RecAction)[keyof typeof RecAction]

    /**
     * @name ReturnNegative
     * @description Return body when calling generateNoImprovement
     */
    export interface ReturnNegative {
        negative: true
        reason: string
    }

    /**
     * @name ReturnSuccess
     * @param TBody generic type corresponds to imp body (required)
     * @description Return body when calling generateImprovements
     */
    export interface ReturnSuccess<TBody> {
        has_many: boolean
        improvements: ReturnSuccessSingle<TBody>[]
    }

    export interface AccountReturnSuccess<TBody> {
        count: number
        hasMany: boolean
        improvements: AccountReturnSuccessSingle<TBody>[]
    }

    /**
     * @name ReturnSuccessSingle
     * @param TBody generic type corresponds to imp body (required)
     * @description Single return body, used within ReturnSuccess
     */
    export interface ReturnSuccessSingle<TBody> {
        priority: number
        improvement: TBody
        rec_action: RecAction
    }

    export interface AccountReturnSuccessSingle<TBody> {
        priority: number
        improvement: TBody
        recAction: RecAction
    }

    /**
     * @name Object
     * @param TBody generic type corresponds to imp body (default=string, json from db)
     * @param TBodyCompact generic type corresponds to smaller imp compact body (not required)
     * @description Properties available on an improvement object (essentially maps to the sql table)
     */
    export interface Object<
        TBody = string | unknown,
        TImprovementType extends Type = 'refresh_cycling_improvements',
    > {
        improvement_id: number
        account_id: Account.ID
        user_id?: User.ID
        unique_key?: string
        batch_id?: number
        hash: string
        title: string
        static_title: string
        // Generic body type, specified per imp e.g. AdjustKeywordBid.Body
        body: TBody
        type: Type
        rec_action: RecAction
        last_updated: Date
        priority: number
        last_read: Date | null
        // TODO: Check if this should be a boolean
        hidden: number
        created: Date
        snoozed_until?: Date | null
        location: TypeLocationRel<TImprovementType>[] // ImprovementLocation[]
        requires_adjust?: boolean
        recheck_start_ts?: Date | null
        recheck_end_ts?: Date | null
        assigned_user_id?: number // Used for issues to track assignment
    }

    export interface AccountObject<TBody = string | unknown> {
        id: UUID
        accountId: Account.ID
        userId: User.ID | null
        platform: Platform.Platform
        title: string
        staticTitle: string
        body: TBody
        type: Type
        recAction: RecAction
        location: TypeLocationRel[]
        priority: number
        requiresAdjust: boolean
        createdAt: Date
        updatedAt: Date
    }

    export type AccountCompletedObject<TBody = string | unknown> = Pick<
        Improvement.AccountObject<TBody>,
        | 'id'
        | 'platform'
        | 'accountId'
        | 'userId'
        | 'type'
        | 'title'
        | 'body'
        | 'recAction'
        | 'location'
        | 'updatedAt'
    > & {
        completedAt: Date
        // TODO
        // hash?: string
        // pushedData?: any
    }

    export type AccountDismissedObject<TBody = string | unknown> = Pick<
        Improvement.AccountObject<TBody>,
        | 'id'
        | 'platform'
        | 'accountId'
        | 'userId'
        | 'type'
        | 'title'
        | 'body'
        | 'recAction'
        | 'location'
    > & {
        updatedAt?: Date
        dismissedAt?: Date
        deferredUntil?: Date
    }

    /**
     * @name CompletedObject
     * @param TBody generic type corresponds to imp body (default=string, json from db)
     * @description Properties available on a completed improvement object
     */
    export type CompletedObject<TBody = string | unknown> = Pick<
        Improvement.Object,
        | 'hash'
        | 'account_id'
        | 'improvement_id'
        | 'rec_action'
        | 'type'
        | 'last_updated'
        | 'title'
        | 'static_title'
        | 'location'
    > & {
        completed_improvement_id: number
        completed_timestamp: Date
        body: TBody
        pushed_data: any
        user_id: User.ID
        recheck_start_ts?: Date | null
        recheck_end_ts?: Date | null
        cleared?: number
    }

    /**
     * @name DismissedObject
     * @param TBody generic type corresponds to imp body (default=string, json from db)
     * @description Properties available on a dismissed improvement object
     */
    export type DismissedObject<TBody = string | unknown> = Pick<
        Improvement.Object,
        | 'hash'
        | 'account_id'
        | 'improvement_id'
        | 'rec_action'
        | 'type'
        | 'last_updated'
        | 'title'
        | 'location'
    > & {
        deferred_timestamp: string
        deferred_until: string
        user_id: User.ID
        body: TBody
        recheck_start_ts?: Date | null
        recheck_end_ts?: Date | null
        cleared?: number
    }

    /**
     * @name AccountLocation
     * @description Used to describe the account location that the improvement is concerned with
     */
    export type AccountLocation<T extends Entity.EntityLocation = Entity.EntityLocation> = {
        entity: T
        label: string
    } & Entity.Ids<T>

    // TODO(deepLinking): is there a better way to do this?
    /**
     * @name TypeLocationRel
     * @description Used to match an improvement type to its locations. Defaults as the meta improvement 'refresh_cycling_improvements'
     */
    export type TypeLocationRel<T extends Improvement.Type = 'refresh_cycling_improvements'> =
        ImprovementLocations[T] extends ImprovementLocationTuple
            ? AccountLocation<ImprovementLocations[T][number]>
            : ImprovementLocations[T] extends ImprovementMultiLocationTuple
              ? AccountLocation<ImprovementLocations[T][number][number]>
              : AccountLocation

    /**
     * @name LookbackWindow
     * @description Describes every generic lookback window
     */
    export enum LookbackWindow {
        'SEVEN_DAYS' = 7,
        'FOURTEEN_DAYS' = 14,
        'THIRTY_DAYS' = 30,
        'FOURTY_FIVE_DAYS' = 45,
        'SIXTY_DAYS' = 60,
        'NINETY_DAYS' = 90,
        'ONE_HUNDRED_EIGHTY_DAYS' = 180,
        'ONE_YEAR' = 365,
    }

    /**
     * @deprecated use `LocationEntity` found in '@opteo/types/entity' instead
     */
    export enum LocationEntity {
        AccountLevel = 'account-level',
        Ad = 'ad',
        AdGroup = 'adgroup',
        AssetGroup = 'asset-group',
        Campaign = 'campaign',
        CampaignGroup = 'campaign-group',
        ConversionAction = 'conversion-action',
        Demographic = 'demographic',
        Device = 'device',
        Experiment = 'experiment',
        Extension = 'extension',
        Keyword = 'keyword',
        Location = 'location',
        NGram = 'n-gram',
        NegativeKeyword = 'negative-keyword',
        NegativeList = 'negative-list',
        Pause = 'pause',
        Placement = 'placement',
        PlacementExclusionList = 'placement-exclusion-list',
        Product = 'product',
        ProductGroup = 'product-group',
        ReplacementKeyword = 'replacement-keyword',
        SearchTerm = 'search-term',
        URL = 'url',
        Word = 'word',
    }

    export interface FormatTitleOptions<TBody> {
        improvement: Improvement.Object<TBody>
    }

    export interface SlackMessage {
        title: string
        text: string
        stats?: Record<string, string | number>
    }

    export interface FormatSlackMessageOptions<TBody> {
        improvement: Improvement.Object<TBody>
        domain_name: string
        currency: string
        formatMoney: (value: number) => string
    }

    export interface AccountFormatSlackMessageOptions<TBody> {
        improvement: Improvement.AccountObject<TBody>
        accountName: string
        currency: Billing.CurrencyCode
        formatMoney: (value: number) => string
    }

    // Raw row returned when querying db.common.improvements
    // Used in improvement.getAllImprovements
    export type RawRow = Omit<
        Improvement.Object<string | undefined>,
        | 'user_id'
        | 'unique_key'
        | 'title'
        | 'static_title'
        | 'status'
        | 'snoozed_until'
        | 'location'
    >

    // Common generic improvement errors (not to be used in improvement implementations)
    export const Error = {
        INVALID_REC_ACTION: 'improvement_invalid_rec_action',
        INVALID_TYPE: 'improvement_invalid_type',
        IMPROVEMENT_NOT_FOUND: 'improvement_not_found',
        READ_ONLY_DOMAIN: 'read_only_domain',
        MISSING_IMPROVEMENT_ID: 'missing_improvement_id',
        MISSING_DOMAIN_ID: 'missing_domain_id',
        MISSING_USER_ID: 'missing_user_id',
        // `unique_fields` must be either "hash" or an array of body keys
        INVALID_UNIQUE_FIELDS: 'invalid_unique_fields',
        INVALID_UNIQUE_FUNCTION: 'invalid_unique_function',
        MISSING_UNIQUE_KEY_GENERATOR: 'missing_unique_key_generator',
        BAD_JSON_IN_BODY: 'bad_json_in_body',
        INVALID_DOMAIN: 'domain_invalid',
        MISSING_ACCOUNT_ID: 'missing_account_id',
        BAD_PLATFORM_FOR_IMP_TYPE: 'bad_platform_for_imp_type',
    } as const

    export type Error = (typeof Error)[keyof typeof Error]

    export type ImplementReturnData = {
        daysToDefer?: number
        issueResolved?: boolean
        improvementHashToRefresh?: string
        improvement?: Improvement.Object
    }

    // Method response type for the implement method
    export type ImplementReturn = ImplementReturnData | void

    export interface UpdateResult {
        inserted_improvement_ids: number[]
        created_improvement_count: number
        updated_improvement_count: number
        ignored_due_to_dismissal_count: number
        deleted_count: number
    }

    // Improvement status value for new Accounts architecture
    export enum Status {
        // Improvement is valid and can be shown to a user
        Active = 0,
        // Improvement has expired and is no longer valid
        Expired = 1,
        // Improvement has been pushed by a user
        Completed = 2,
        // Improvement has been dismissed temporarily by a user
        Deferred = 3,
    }

    export enum DeferDuration {
        Week = 'week',
        Month = 'month',
        Forever = 'forever',
    }

    export type ImprovementPayloadMap = {
        ACCOUNT_LEVEL_EXTENSIONS:
            | AddCallExtension.Body
            | AddCallExtension.Body
            | AddSitelinkExtension.Body
            | AddStructuredSnippetsExtension.Body
        AD_COMPARISON_V2: PauseAd.Body
        ADJUST_BID: AdjustAgeBid.AdjustAgeBidBody
        ADJUST_ATTRIBUTION_MODEL: AdjustAttributionModel.Body
        BIDS_BELOW_FIRST_PAGE: FixBelowFirstPageBid.Body
        BROAD_KEYWORDS: RemoveBroadKeywords.Body
        REPLACE_BROAD_KEYWORDS_V2: ReplaceBroadKeywordV2.Body
        BROKEN_LINK: FixBrokenLink.Body
        BUDGET_CAP: ReduceCampaignBids.Body | LiftCampaignBudgetCap.Body
        CAMPAIGN_LEVEL_EXTENSIONS: CampaignLevelExtensions.Body
        CHECK_PRODUCT_GROUPS:
            | CheckProductGroups.AdjustProductGroupBid.Body
            | CheckProductGroups.ResyncProductGroup.Body
        CONTEXT_EXCLUSIONS: ContentExclusion.ImpTypes.Body
        DEMO_BIDS: DemoBids.DemoBodies[number]
        DEVICE_BID: AdjustDeviceBid.Body
        DEVICE_BID_V2: AdjustDeviceBidsV2.DeviceBidsRecActions
        DUPLICATE_KEYWORDS: DuplicateKeywords.Body
        GENDER_BID: AdjustGenderBid.AdjustGenderBidBody
        GEO_BIDS: AdjustGeoBids.Body
        GOOGLE_SYNC: GoogleAdsSync.Body
        KEYWORD_BIDS_V2: AdjustKeywordBidV2.Body
        KEYWORD_CUTOFF: PauseKeyword.Body
        LOCATION_BIDS: LocationBids.Body
        LOCATION_INTERESTS: DisableInterestLocations.Body
        LOCATION_INTERESTS_V2: DisableInterestLocationsV2.Body
        MOBILE_APPS_EXCLUSION: ExcludeMobileApps.Body
        NEGATIVE_GEO_KEYWORDS: AddNegativeGeoKeyword.Body
        NGRAM: Ngram.NgramImpType.Body
        PARENTAL_BID: AdjustParentalBid.AdjustParentalBidBody
        PLACEMENT_EXCLUSION: ExcludePlacement.Body
        REMOVE_PLUSSED_NEGATIVES: RemovePlussedNegatives.Body
        ROBOT_NEGATIVES: AddRobotNegative.AddRobotNegativeBody
        SEARCH_PARTNER_SETTINGS: SearchPartnerSettings.Body
        SEARCH_PARTNERS: DisableSearchPartners.ImpTypes.Body
        SETTING_FLIPPERS: SettingFlippers.Body
        SITE_CATEGORY_EXCLUSIONS: AddSiteExclusions.Body
        SKAG_V2: AddSkagV2.AddSkagV2Body
        SPELL_CHECKER: CheckAdSpelling.CheckAdSpellingBody
        GA_SQR: CheckQueryRelevance.Body
        MS_SQR: CheckQueryRelevanceMS.Body
        SYNC: PauseCampaignSyncImprovement.Body | KeywordBidsSyncImprovement.Body
        TARGET_ADJUSTMENTS: AdjustTarget.Body | AdjustAdGroupMultiTargets.Body
    }
}

// Types used in meta.ts
export namespace Meta {
    export interface Campaign {
        metrics: Pick<common.Metrics, 'cost_micros'>
    }

    export interface Result {
        code: 'enqueued_jobs' | 'no_campaigns' | 'no_cost'
        n_hashes: number
    }
}

// Types used in improvements.ts (for listing imps)
export namespace ImprovementList {
    export interface ImpCountsPerDomain {
        [domain_id: Account.ID]: {
            inbox: number
            completed: number
            dismissed: number
        }
    }

    interface UserAccountId {
        user_id: User.ID
        account_id: Account.ID
    }

    export interface ImpPreferencesRaw extends UserAccountId {
        improvements_to_ignore: Improvement.RecAction[]
        username: string
    }

    export interface DuplicateTypes {
        [unique_identifier: string]: number
    }

    export interface GroupedImprovement {
        [key: string]: Improvement.RecAction[]
    }

    export interface RecActionPreferences {
        rec_actions: Improvement.RecAction[]
        static_title: string
        selected: boolean
    }

    export interface UserDomainIgnoredImprovements extends UserAccountId {
        ignored_rec_actions: Improvement.RecAction[]
    }

    export interface UserDomainPreferences extends UserAccountId {
        rec_action_preferences: RecActionPreferences[]
    }
}

export interface RefreshImprovementObject {
    id: string // status id
    improvementId: string
    status: Improvement.Status
    uniqueKey: string
    deferredUntil: Date | null
}

export type ImprovementCheckType =
    | '_base' // used to mark that the improvment or issue was refreshed at all
    | 'ngram'
    | 'search_term'
    | 'campaign_group'
    | 'campaign'
    | 'ad_group'
    | 'negative_keyword'
    | 'positive_keyword'
    | 'recent_bid_change'
    | 'positive_location_target'
    | 'location_served'
    | 'negative_list'
    | 'income_range'
    | 'parental_status'
    | 'age_range'
    | 'device'
    | 'gender'
    | 'placement'
    | 'mobile_app_placement'
    | 'youtube_placement'
    | 'website_placement'
    | 'frequency_cap'
    | 'budget'
    | 'target'
    | 'search_partner_segment'
    | 'match_type'
    | 'ad_group_criterion'
    | 'campaign_criterion'
    | 'ad_group_bid_modifier'
    | 'ad'
    | 'landing_page'
    | 'company'
    | 'company_size'
    | 'city'
    | 'country'
    | 'day_of_week'
    | 'industry'
    | 'job_function'
    | 'job_title'
    | 'seniority'
    | 'network'
    | 'state'
    | 'advertising_channel'
    | 'hour_of_day'
    | 'product'

export interface ImprovementCheck {
    recAction: Improvement.RecAction
    label: string
    checks: { checkType: ImprovementCheckType; count: number; timestamp: Date }[]
    improvementCategory: Improvement.Category
    improvementsGenerated: number
}

export const RecActionToTypeMap: Record<Improvement.RecAction, Improvement.Type> = {
    [Improvement.RecAction.PauseAd]: Improvement.Type.AdComparison,
    [Improvement.RecAction.WriteAd]: Improvement.Type.AdComparison,
    [Improvement.RecAction.PauseAdV2]: Improvement.Type.AdComparisonV2,
    [Improvement.RecAction.WriteAdV2]: Improvement.Type.AdComparisonV2,
    [Improvement.RecAction.CreateBrandCampaign]: Improvement.Type.Branding,
    [Improvement.RecAction.CreateRemarketingCampaign]: Improvement.Type.Remarketing,
    [Improvement.RecAction.RemoveBroadKeywords]: Improvement.Type.BroadKeywords,
    [Improvement.RecAction.RemoveBroadKeywordsV2]: Improvement.Type.BroadKeywordsV2,
    [Improvement.RecAction.LiftCampaignBudgetCap]: Improvement.Type.BudgetCap,
    [Improvement.RecAction.ReduceCampaignBids]: Improvement.Type.BudgetCap,
    [Improvement.RecAction.CheckQueryRelevance]: Improvement.Type.SQR,
    [Improvement.RecAction.AddNegativeListShopping]: Improvement.Type.SqrShopping,
    [Improvement.RecAction.AddSkagV1]: Improvement.Type.SKAGV1,
    [Improvement.RecAction.AddSkagV2]: Improvement.Type.SKAG,
    [Improvement.RecAction.DuplicateKeyword]: Improvement.Type.NotExists,
    [Improvement.RecAction.PauseKeyword]: Improvement.Type.KeywordCutoff,
    [Improvement.RecAction.AdjustKeywordBid]: Improvement.Type.KeywordBids,
    [Improvement.RecAction.PauseKeywordV2]: Improvement.Type.KeywordBidsV2,
    [Improvement.RecAction.AdjustKeywordBidV2]: Improvement.Type.KeywordBidsV2,
    [Improvement.RecAction.IncreaseKeywordBidV2]: Improvement.Type.KeywordBidsV2,
    [Improvement.RecAction.FixBrokenLink]: Improvement.Type.BrokenLink,
    [Improvement.RecAction.FixBrokenSitelink]: Improvement.Type.BrokenLink,
    [Improvement.RecAction.AdjustGenderBid]: Improvement.Type.DemographicGenderMod,
    [Improvement.RecAction.AdjustParentalBid]: Improvement.Type.DemographicParentalMod,
    [Improvement.RecAction.AdjustAgeBid]: Improvement.Type.DemographicAgeMod,
    [Improvement.RecAction.ReduceNgramBids]: Improvement.Type.Ngram,
    [Improvement.RecAction.AddNegativeNgram]: Improvement.Type.Ngram,
    [Improvement.RecAction.ExcludePlacement]: Improvement.Type.PlacementExclusion,
    [Improvement.RecAction.ExcludeMobileApps]: Improvement.Type.MobileAppsExclusion,
    [Improvement.RecAction.PauseDuplicateKeywords]: Improvement.Type.PauseDuplicateKeywords,
    [Improvement.RecAction.PauseDuplicateKeywordsV2]: Improvement.Type.PauseDuplicateKeywordsV2,
    [Improvement.RecAction.AddSiteExclusions]: Improvement.Type.SiteCategoryExclusions,
    [Improvement.RecAction.AddFrequencyCapping]: Improvement.Type.FrequencyCapping,
    [Improvement.RecAction.SearchPartnersSettings]: Improvement.Type.SearchPartnersSettings,
    [Improvement.RecAction.DisableSearchPartners]: Improvement.Type.SearchPartners,
    [Improvement.RecAction.CheckAdSpelling]: Improvement.Type.SpellChecker,
    [Improvement.RecAction.AdjustAdScheduleBids]: Improvement.Type.AdSchedule,
    [Improvement.RecAction.AdjustAdScheduleBidsV2]: Improvement.Type.AdScheduleV2,
    [Improvement.RecAction.RemovePlussedNegatives]: Improvement.Type.RemovePlussedNegatives,
    [Improvement.RecAction.FixNegativesBlockingKeywords]:
        Improvement.Type.CheckNegativesBlockingKeywords,
    [Improvement.RecAction.AdjustGeoBids]: Improvement.Type.GeoBids,
    [Improvement.RecAction.AdjustLocationBids]: Improvement.Type.LocationBids,
    [Improvement.RecAction.ExcludeLocation]: Improvement.Type.LocationBids,
    [Improvement.RecAction.AddMissingCampaignsToSharedSet]:
        Improvement.Type.ResyncSharedSetNegativeLists,
    [Improvement.RecAction.DisableInterestLocations]: Improvement.Type.LocationInterests,
    [Improvement.RecAction.DisableInterestLocationsV2]: Improvement.Type.LocationInterestV2,
    [Improvement.RecAction.AddLocationNameAsNegative]: Improvement.Type.NegativeGeoKeywords,
    [Improvement.RecAction.FixBelowFirstPageBid]: Improvement.Type.BidsBelowFirstPage,
    [Improvement.RecAction.AddCallExtension]: Improvement.Type.AccountLevelExtensions,
    [Improvement.RecAction.AddCalloutExtension]: Improvement.Type.AccountLevelExtensions,
    [Improvement.RecAction.AddSitelinkExtension]: Improvement.Type.AccountLevelExtensions,
    [Improvement.RecAction.AddStructuredSnippetsExtension]: Improvement.Type.AccountLevelExtensions,
    [Improvement.RecAction.AddCampaignLevelSitelinkExtension]:
        Improvement.Type.CampaignLevelExtensions,
    [Improvement.RecAction.AddCampaignLevelCalloutExtension]:
        Improvement.Type.CampaignLevelExtensions,
    [Improvement.RecAction.AddCampaignLevelSnippetExtension]:
        Improvement.Type.CampaignLevelExtensions,
    [Improvement.RecAction.ResyncExclusionLists]: Improvement.Type.ResyncExclusionLists,
    [Improvement.RecAction.AdjustAttributionModel]: Improvement.Type.AttributionModel,
    [Improvement.RecAction.AdjustDeviceBid]: Improvement.Type.DeviceBids,
    [Improvement.RecAction.AdjustProductGroupBid]: Improvement.Type.CheckProductGroups,
    [Improvement.RecAction.ResyncProductGroups]: Improvement.Type.CheckProductGroups,
    [Improvement.RecAction.SubdivideProductGroup]: Improvement.Type.CheckProductGroups,
    [Improvement.RecAction.AddRobotNegative]: Improvement.Type.RobotNegatives,
    [Improvement.RecAction.ExcludeWebsitePlacement]: Improvement.Type.ContentExclusions,
    [Improvement.RecAction.ExcludeMobileAppPlacement]: Improvement.Type.ContentExclusions,
    [Improvement.RecAction.ExcludeYoutubePlacement]: Improvement.Type.ContentExclusions,
    [Improvement.RecAction.ExcludeGmailPlacement]: Improvement.Type.ContentExclusions,
    [Improvement.RecAction.AdjustSingleAgeBid]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AdjustSingleGenderBid]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AdjustSingleParentalStatusBid]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AdjustSingleIncomeBid]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.ExcludeAgeDemographic]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.ExcludeGenderDemographic]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.ExcludeParentalStatusDemographic]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.ExcludeIncomeDemographic]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AdjustMultipleAgeBids]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AdjustMultipleGenderBids]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AdjustMultipleParentalStatusBids]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AdjustMultipleIncomeBids]: Improvement.Type.DemographicBids,
    [Improvement.RecAction.AddNegativeWord]: Improvement.Type.NotExists,
    [Improvement.RecAction.ReduceWordBids]: Improvement.Type.NotExists,
    [Improvement.RecAction.AdjustAdGroupMultiTargets]: Improvement.Type.TargetAdjustments,
    [Improvement.RecAction.CheckQueryRelevanceMs]: Improvement.Type.SQRMs,
    [Improvement.RecAction.PauseKeywordMs]: Improvement.Type.KeywordBidsMs,
    [Improvement.RecAction.AdjustKeywordBidMs]: Improvement.Type.KeywordBidsMs,
    [Improvement.RecAction.IncreaseKeywordBidMs]: Improvement.Type.KeywordBidsMs,
    [Improvement.RecAction.AdjustSingleDeviceBid]: Improvement.Type.DeviceBidsV2,
    [Improvement.RecAction.AdjustMultipleDeviceBids]: Improvement.Type.DeviceBidsV2,
    [Improvement.RecAction.ExcludeSingleDevice]: Improvement.Type.DeviceBidsV2,
    [Improvement.RecAction.RecheckDisapprovedAd]: Improvement.Type.DisapprovedEntityIssue,
    [Improvement.RecAction.RecheckDisapprovedKeyword]: Improvement.Type.DisapprovedEntityIssue,
    [Improvement.RecAction.RecheckEmptyCampaign]: Improvement.Type.EmptyEntityIssue,
    [Improvement.RecAction.RecheckEmptyAdGroup]: Improvement.Type.EmptyEntityIssue,
    [Improvement.RecAction.RecheckBrokenLink]: Improvement.Type.BrokenLinkIssue,
    [Improvement.RecAction.RecheckFailedLearning]: Improvement.Type.FailedLearningIssue,
    [Improvement.RecAction.RecheckSpellingError]: Improvement.Type.SpellingErrorIssue,
    [Improvement.RecAction.RecheckReactivatedKeyword]:
        Improvement.Type.LowActivityReactivationIssue,
    [Improvement.RecAction.RecheckReactivatedAdGroup]:
        Improvement.Type.LowActivityReactivationIssue,
    [Improvement.RecAction.DisableImageTouchups]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.DisableImageTemplates]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.DisableTextOptimizations]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.DisableVideoAutoCrop]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.GADisableAdGroupOptimizedTargetingEnabled]:
        Improvement.Type.SettingFlippers,
    [Improvement.RecAction.GADisableBroadMatchAsDefault]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.GAOptOutTextAssetAutomation]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.GAAccountRecommendLimitedBrandSafetySuitability]:
        Improvement.Type.SettingFlippers,
    [Improvement.RecAction.GAAccountToggleRecommendationSubscriptions]:
        Improvement.Type.SettingFlippers,
    [Improvement.RecAction.DisableAudienceExpansionLi]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.AdjustCreativeSelectionLi]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.AdjustAdTargetingFacetLocationLi]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.MSAccountGetAutoApplyOptInStatus]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.MSCampaignPeopleInOrSearchingForOrViewingPages]:
        Improvement.Type.SettingFlippers,
    [Improvement.RecAction.MSCampaignAutoGeneratedAssetsEnabled]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.MSCampaignAutoGeneratedImageOptOut]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.MSCampaignAutoGeneratedTextOptOut]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.MSCampaignFinalUrlExpansionOptOut]: Improvement.Type.SettingFlippers,
    [Improvement.RecAction.ExcludeSegment]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCountryMS]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCountryME]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCountryLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentDeviceGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentDeviceMS]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentDeviceME]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentAgeDemoMS]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentAgeDemoME]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentGenderMS]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentGenderME]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentStateMS]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentStateGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentStateLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCompanyLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCompanySizeLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentMemberIndustryLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentMemberJobFunctionLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentMemberJobTitleLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentMemberSeniorityLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentContentNetworkGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentSearchPartnersGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentOffsiteDeliveryLI]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentSyndicatedSearchMS]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentPublisherPlatformME]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCountryGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCityGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentCityMS]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentAgeDemoGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentGenderGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentIncomeGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentParentalStatusGA]: Improvement.Type.SegmentExclusions,
    [Improvement.RecAction.ExcludeSegmentLandingPageGA]: Improvement.Type.SegmentExclusions,
}

// See https://www.notion.so/opteo/ccffae6703244769b73d7cbdda996ce2?v=d028dfa2c18f42d495e8731ca0710284 for a source of truth
// This also deternmines the order of the improvement checks in the UI
export const RecActionToCategoryMap: Record<Improvement.RecAction, Improvement.Category> = {
    [Improvement.RecAction.AdjustSingleAgeBid]: 'adjustments',
    [Improvement.RecAction.AdjustSingleGenderBid]: 'adjustments',
    [Improvement.RecAction.AdjustSingleIncomeBid]: 'adjustments',
    [Improvement.RecAction.AdjustSingleParentalStatusBid]: 'adjustments',
    [Improvement.RecAction.AdjustMultipleAgeBids]: 'adjustments',
    [Improvement.RecAction.AdjustMultipleGenderBids]: 'adjustments',
    [Improvement.RecAction.AdjustMultipleIncomeBids]: 'adjustments',
    [Improvement.RecAction.AdjustMultipleParentalStatusBids]: 'adjustments',
    [Improvement.RecAction.AdjustSingleDeviceBid]: 'adjustments',
    [Improvement.RecAction.AdjustMultipleDeviceBids]: 'adjustments',
    [Improvement.RecAction.AdjustLocationBids]: 'adjustments',
    [Improvement.RecAction.AddFrequencyCapping]: 'adjustments',
    [Improvement.RecAction.LiftCampaignBudgetCap]: 'adjustments',
    [Improvement.RecAction.ReduceCampaignBids]: 'adjustments',
    [Improvement.RecAction.ReduceNgramBids]: 'adjustments',
    [Improvement.RecAction.AdjustAdGroupMultiTargets]: 'adjustments',
    [Improvement.RecAction.DisableImageTouchups]: 'adjustments',
    [Improvement.RecAction.DisableImageTemplates]: 'adjustments',
    [Improvement.RecAction.DisableTextOptimizations]: 'adjustments',
    [Improvement.RecAction.DisableVideoAutoCrop]: 'adjustments',
    [Improvement.RecAction.GADisableAdGroupOptimizedTargetingEnabled]: 'adjustments',
    [Improvement.RecAction.GADisableBroadMatchAsDefault]: 'adjustments',
    [Improvement.RecAction.GAOptOutTextAssetAutomation]: 'adjustments',
    [Improvement.RecAction.GAAccountRecommendLimitedBrandSafetySuitability]: 'adjustments',
    [Improvement.RecAction.GAAccountToggleRecommendationSubscriptions]: 'adjustments',
    [Improvement.RecAction.DisableAudienceExpansionLi]: 'adjustments',
    [Improvement.RecAction.AdjustCreativeSelectionLi]: 'adjustments',
    [Improvement.RecAction.AdjustAdTargetingFacetLocationLi]: 'adjustments',
    [Improvement.RecAction.MSAccountGetAutoApplyOptInStatus]: 'adjustments',
    [Improvement.RecAction.MSCampaignPeopleInOrSearchingForOrViewingPages]: 'adjustments',
    [Improvement.RecAction.MSCampaignAutoGeneratedAssetsEnabled]: 'adjustments',
    [Improvement.RecAction.MSCampaignAutoGeneratedImageOptOut]: 'adjustments',
    [Improvement.RecAction.MSCampaignAutoGeneratedTextOptOut]: 'adjustments',
    [Improvement.RecAction.MSCampaignFinalUrlExpansionOptOut]: 'adjustments',

    [Improvement.RecAction.ExcludeAgeDemographic]: 'exclusions',
    [Improvement.RecAction.ExcludeGenderDemographic]: 'exclusions',
    [Improvement.RecAction.ExcludeIncomeDemographic]: 'exclusions',
    [Improvement.RecAction.ExcludeParentalStatusDemographic]: 'exclusions',
    [Improvement.RecAction.ExcludeYoutubePlacement]: 'exclusions',
    [Improvement.RecAction.ExcludeGmailPlacement]: 'exclusions',
    [Improvement.RecAction.ExcludeWebsitePlacement]: 'exclusions',
    [Improvement.RecAction.ExcludeMobileAppPlacement]: 'exclusions',
    [Improvement.RecAction.ExcludeSingleDevice]: 'exclusions',
    [Improvement.RecAction.ExcludeLocation]: 'exclusions',
    [Improvement.RecAction.DisableInterestLocationsV2]: 'exclusions',
    [Improvement.RecAction.DisableSearchPartners]: 'exclusions',

    [Improvement.RecAction.CheckQueryRelevance]: 'keywords',
    [Improvement.RecAction.CheckQueryRelevanceMs]: 'keywords',
    [Improvement.RecAction.PauseKeywordV2]: 'keywords',
    [Improvement.RecAction.PauseKeywordMs]: 'keywords',
    [Improvement.RecAction.FixNegativesBlockingKeywords]: 'keywords',
    [Improvement.RecAction.AdjustKeywordBidV2]: 'keywords',
    [Improvement.RecAction.AdjustKeywordBidMs]: 'keywords',
    [Improvement.RecAction.IncreaseKeywordBidV2]: 'keywords',
    [Improvement.RecAction.IncreaseKeywordBidMs]: 'keywords',
    [Improvement.RecAction.RemoveBroadKeywordsV2]: 'keywords',
    [Improvement.RecAction.PauseDuplicateKeywordsV2]: 'keywords',

    [Improvement.RecAction.AddRobotNegative]: 'negatives',
    [Improvement.RecAction.AddLocationNameAsNegative]: 'negatives',
    [Improvement.RecAction.AddNegativeNgram]: 'negatives',
    [Improvement.RecAction.AddMissingCampaignsToSharedSet]: 'negatives',
    [Improvement.RecAction.ResyncExclusionLists]: 'negatives',

    [Improvement.RecAction.DuplicateKeyword]: 'deprecated',
    [Improvement.RecAction.ReduceWordBids]: 'deprecated',
    [Improvement.RecAction.FixBelowFirstPageBid]: 'deprecated',
    [Improvement.RecAction.AddCampaignLevelCalloutExtension]: 'deprecated',
    [Improvement.RecAction.AddCampaignLevelSitelinkExtension]: 'deprecated',
    [Improvement.RecAction.AddCampaignLevelSnippetExtension]: 'deprecated',
    [Improvement.RecAction.AddNegativeWord]: 'deprecated',
    [Improvement.RecAction.AddSiteExclusions]: 'deprecated',
    [Improvement.RecAction.AddSitelinkExtension]: 'deprecated',
    [Improvement.RecAction.AddSkagV1]: 'deprecated',
    [Improvement.RecAction.AddSkagV2]: 'deprecated',
    [Improvement.RecAction.AddStructuredSnippetsExtension]: 'deprecated',
    [Improvement.RecAction.AdjustAdScheduleBids]: 'deprecated',
    [Improvement.RecAction.AdjustAdScheduleBidsV2]: 'deprecated',
    [Improvement.RecAction.AdjustKeywordBid]: 'deprecated',
    [Improvement.RecAction.CheckAdSpelling]: 'deprecated',
    [Improvement.RecAction.CreateBrandCampaign]: 'deprecated',
    [Improvement.RecAction.CreateRemarketingCampaign]: 'deprecated',
    [Improvement.RecAction.FixBrokenLink]: 'deprecated',
    [Improvement.RecAction.FixBrokenSitelink]: 'deprecated',
    [Improvement.RecAction.PauseAd]: 'deprecated',
    [Improvement.RecAction.PauseAdV2]: 'deprecated',
    [Improvement.RecAction.RemovePlussedNegatives]: 'deprecated',
    [Improvement.RecAction.WriteAd]: 'deprecated',
    [Improvement.RecAction.WriteAdV2]: 'deprecated',
    [Improvement.RecAction.ResyncProductGroups]: 'deprecated',
    [Improvement.RecAction.AddCallExtension]: 'deprecated',
    [Improvement.RecAction.AddCalloutExtension]: 'deprecated',
    [Improvement.RecAction.PauseDuplicateKeywords]: 'deprecated',
    [Improvement.RecAction.PauseKeyword]: 'deprecated',
    [Improvement.RecAction.RemoveBroadKeywords]: 'deprecated',
    [Improvement.RecAction.AddNegativeListShopping]: 'deprecated',
    [Improvement.RecAction.AdjustAgeBid]: 'deprecated',
    [Improvement.RecAction.AdjustAttributionModel]: 'deprecated',
    [Improvement.RecAction.AdjustDeviceBid]: 'deprecated',
    [Improvement.RecAction.AdjustGenderBid]: 'deprecated',
    [Improvement.RecAction.AdjustGeoBids]: 'deprecated',
    [Improvement.RecAction.AdjustParentalBid]: 'deprecated',
    [Improvement.RecAction.AdjustProductGroupBid]: 'deprecated',
    [Improvement.RecAction.DisableInterestLocations]: 'deprecated',
    [Improvement.RecAction.SearchPartnersSettings]: 'deprecated',
    [Improvement.RecAction.SubdivideProductGroup]: 'deprecated',
    [Improvement.RecAction.ExcludePlacement]: 'deprecated',
    [Improvement.RecAction.ExcludeMobileApps]: 'deprecated',
    [Improvement.RecAction.RecheckDisapprovedAd]: 'disapprovals',
    [Improvement.RecAction.RecheckDisapprovedKeyword]: 'disapprovals',
    [Improvement.RecAction.RecheckBrokenLink]: 'broken_links',
    [Improvement.RecAction.RecheckSpellingError]: 'spelling_errors',
    [Improvement.RecAction.RecheckEmptyCampaign]: 'account_management',
    [Improvement.RecAction.RecheckEmptyAdGroup]: 'account_management',
    [Improvement.RecAction.RecheckFailedLearning]: 'account_management',
    [Improvement.RecAction.RecheckReactivatedKeyword]: 'account_management',
    [Improvement.RecAction.RecheckReactivatedAdGroup]: 'account_management',

    [Improvement.RecAction.ExcludeSegment]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCountryMS]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCountryME]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCountryLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentDeviceGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentDeviceMS]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentDeviceME]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentAgeDemoMS]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentAgeDemoME]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentGenderMS]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentGenderME]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentStateMS]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentStateGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentStateLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCompanyLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCompanySizeLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentMemberIndustryLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentMemberJobFunctionLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentMemberJobTitleLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentMemberSeniorityLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentContentNetworkGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentSearchPartnersGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentOffsiteDeliveryLI]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentSyndicatedSearchMS]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentPublisherPlatformME]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCountryGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCityGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentCityMS]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentAgeDemoGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentGenderGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentIncomeGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentParentalStatusGA]: 'exclusions',
    [Improvement.RecAction.ExcludeSegmentLandingPageGA]: 'exclusions',
} as const

export const ImprovementTypeToCategoryMap: Record<Improvement.Type, Improvement.Category> =
    Object.fromEntries(
        Object.values(Improvement.Type).map((type: Improvement.Type) => {
            const invertedRecActionToTypeMap = Object.fromEntries(
                Object.entries(RecActionToTypeMap).map(([key, value]) => [value, key])
            )
            const firstRecAction = invertedRecActionToTypeMap[type] as Improvement.RecAction
            return [type, RecActionToCategoryMap[firstRecAction] ?? 'deprecated'] satisfies [
                Improvement.Type,
                Improvement.Category,
            ]
        })
    ) as Record<Improvement.Type, Improvement.Category>
